import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  X,
  LogOut,
  Folder,
  TrendingUp,
  Plus,
  List,
  User,
  Users,
  Settings,
  Link2,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { removeAllCookies } from "../../../utils/auth";
import routes from "../../../routes/routes";
import axios from "axios";
import { AUTH_URL } from "../../../utils/url";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Spinner } from "../../ui/spinner";
import { getConfig } from "../../../utils/permissions";
import Avatar from "../avatar/avatar";
import { AuthContext } from "../../../context/auth";

export default function Navigation({
  navigationExpanded,
  setNavigationExpanded,
  activeNav,
  setActiveNav,
  setPageTitle,
}) {
  const [logoutInProgress, setLogoutInProgress] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { loginData } = useContext(AuthContext);

  const navigation = [
    {
      id: 1,
      title: "All Releases",
      icon: <Folder className="h-4 w-4" />,
      route: routes.allReleases,
      hidden: !getConfig("privilege.can-view-all-releases"),
    },
    {
      id: 2,
      title: "My Releases",
      icon: <List className="h-4 w-4" />,
      route: routes.myReleases,
    },
    {
      id: 3,
      title: "New Release",
      icon: <Plus className="h-4 w-4" />,
      route: routes.newRelease,
    },
    {
      id: 4,
      title: "My Profile",
      icon: <User className="h-4 w-4" />,
      route: routes.me,
    },
    {
      id: 5,
      title: "Analytics & Reports",
      icon: <TrendingUp className="h-4 w-4" />,
      route: "https://reports.rayapprelease.com/login",
    },
    {
      id: 6,
      title: "Users",
      icon: <Users className="h-4 w-4" />,
      route: routes.users,
      hidden: !getConfig("privilege.can-configure-application"),
    },
    {
      id: 7,
      title: "Configure",
      icon: <Settings className="h-4 w-4" />,
      route: routes.configure,
      hidden: !getConfig("privilege.can-configure-application"),
    },
    {
      id: 8,
      title: "PDL Cosmos",
      icon: <Link2 className="h-4 w-4" />,
      route: "http://stage-pdl-cms-adv.s3-website.ap-south-1.amazonaws.com",
      hidden: !getConfig("privilege.can-view-all-releases"),
    },
  ];

  const changeActiveNav = (nav) => {
    if (location.pathname === "/" || nav.disabled === true) return;
    setActiveNav(nav.id);
    let activeNavObject = navigation.find((n) => n.id === nav.id);
    setPageTitle(activeNavObject.title);

    const mobileMedia = window.matchMedia("only screen and (max-width:960px)");
    const portraitMedia = window.matchMedia("(orientation: portrait)");

    if (mobileMedia.matches || portraitMedia.matches) {
      setNavigationExpanded(false);
    }
  };

  const logout = async () => {
    try {
      setLogoutInProgress(true);
      const logoutApi = AUTH_URL + "/user/logout";
      await axios.get(logoutApi, { withCredentials: true });
    } catch (e) {
      console.error(e);
    } finally {
      setLogoutInProgress(false);
      removeAllCookies();
      navigate(routes.root);
    }
  };

  useEffect(() => {
    let path = location.pathname;
    let activeMenu = navigation.find((nav) => nav.route === path);

    if (activeMenu) {
      setActiveNav(activeMenu.id);
      setPageTitle(activeMenu.title);
    }
    //eslint-disable-next-line
  }, [location]);

  useEffect(() => {
    if (window.visualViewport.width < 960) setNavigationExpanded(false);
    // eslint-disable-next-line
  }, []);

  return navigationExpanded === true ? (
    <div className="fixed left-0 top-0 md:relative z-50 h-full w-80 bg-white shadow-md border-r flex flex-col">
      <div className="flex-1">
        <div className="p-4 border-b">
          <Button
            variant="ghost"
            size="icon"
            className="mb-4 md:hidden"
            onClick={() => setNavigationExpanded(false)}
          >
            <X className="h-4 w-4" />
          </Button>
          <div className="flex flex-col items-center gap-3">
            <img
              src="https://www.rayapprelease.com/assets/images/logo-main.png"
              alt="RayApp Release"
              className="w-36 h-auto"
            />
            <div className="text-base">
              Release Hub
              <span className="text-xs align-super text-muted-foreground">
                Beta
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-2">
          {navigation.map((n) => {
            if ("hidden" in n && !loginData) {
              return null;
            }
            if (n.hidden) {
              return null;
            }
            const disabled =
              location.pathname === routes.root || n.disabled === true;

            const buttonContent = (
              <Button
                variant={activeNav === n.id ? "destructive" : "ghost"}
                className={`w-full justify-start gap-2 ${
                  disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={() =>
                  n.route.startsWith("/") && !disabled && changeActiveNav(n)
                }
                key={n.id}
                disabled={disabled}
              >
                {n.icon}
                {n.title}
                {n.disabled && (
                  <Badge variant="outline" className="ml-auto text-xs">
                    Coming Soon
                  </Badge>
                )}
              </Button>
            );

            return n.route.startsWith("/") ? (
              <Link key={n.id} to={!disabled ? n.route : "#"} className="block">
                {buttonContent}
              </Link>
            ) : (
              <a
                key={n.id}
                href={n.route}
                target="_blank"
                rel="noreferrer"
                className="block"
              >
                {buttonContent}
              </a>
            );
          })}
        </div>
      </div>

      <div className="p-4 border-t bg-gray-50">
        {loginData && loginData.user._id ? (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm text-muted-foreground">Hello,</span>
            <Avatar />
            <Link
              to={routes.me}
              className="text-sm font-medium hover:underline !text-foreground"
            >
              {loginData.user.name ||
                loginData.user.firstName + " " + loginData.user.lastName}
            </Link>
          </div>
        ) : (
          <div className="flex justify-center mb-3">
            <Spinner />
          </div>
        )}

        {logoutInProgress ? (
          <div className="flex justify-center">
            <Spinner />
          </div>
        ) : (
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={logout}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        )}
      </div>
    </div>
  ) : null;
}
